export const WHOLESALE_DELIVERY_TIERS = [
  { id: 'no_delivery', label: 'Accessories only: no unit delivery', firstUnitCents: 0, additionalUnitCents: 0 },
  { id: 'ac_25_5', label: 'AC order: $25 first + $5 additional', firstUnitCents: 2500, additionalUnitCents: 500 },
  { id: 'standard_30_8', label: 'Standard order: $30 first + $8 additional', firstUnitCents: 3000, additionalUnitCents: 800 },
  { id: 'large_40_16', label: 'Large-unit order: $40 first + $16 additional', firstUnitCents: 4000, additionalUnitCents: 1600 },
]

const tierById = new Map(WHOLESALE_DELIVERY_TIERS.map((tier) => [tier.id, tier]))

const dollarsToCents = (value) => {
  const normalized = String(value ?? '').replace(/[$,]/g, '').trim()
  if (!normalized) return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed * 100) : null
}

const productCostCents = (product) => {
  if (product?.costCents === null || product?.costCents === undefined || product?.costCents === '') return null
  const parsed = Number(product.costCents)
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : null
}

export const isNonUnitAccessory = (product = {}) => {
  const safeProduct = product || {}
  const type = String(safeProduct.type || '').toLowerCase()
  const description = String(safeProduct.description || '').toLowerCase()
  const accessoryType = /\b(accessor(?:y|ies)|bracket|installation parts?|mount(?:ing)?|parts?|pedestal|sleeve|trim kit)\b/.test(type)
  const accessoryDescription = /\b(bracket|wall sleeve|sleeve for|mount(?:ing)? (?:bracket|kit)|trim kit|stacking kit|pedestal|cover for|replacement (?:filter|panel|rack|remote)|power cord|drain hose|connector kit)\b/.test(description)
  return accessoryType || accessoryDescription
}

export const isAirConditioner = (product = {}) => {
  if (isNonUnitAccessory(product)) return false
  const safeProduct = product || {}
  const searchable = `${safeProduct.type || ''} ${safeProduct.description || ''}`.toLowerCase()
  return /air\s*condition|\ba\s*\/\s*c\b|\bac\b|\bptac\b|mini[ -]?split/.test(searchable)
}

const getRowCosts = (row = {}) => {
  const pmUnitCostCents = dollarsToCents(row.enteredUnitCost) ?? productCostCents(row.currentProduct)
  const wholesaleUnitCostCents = productCostCents(row.otherProduct)
  const quantity = Number(row.quantity) || 0
  return {
    pmUnitCostCents,
    wholesaleUnitCostCents,
    pmLineTotalCents: pmUnitCostCents === null ? null : Math.round(pmUnitCostCents * quantity),
    wholesaleLineTotalCents: wholesaleUnitCostCents === null ? null : Math.round(wholesaleUnitCostCents * quantity),
  }
}

const getRecommendedSource = (row) => {
  if (row.alreadySourced) return 'existing'
  const { pmLineTotalCents, wholesaleLineTotalCents } = getRowCosts(row)
  if (pmLineTotalCents === null) return null
  if (wholesaleLineTotalCents !== null && wholesaleLineTotalCents <= pmLineTotalCents) return 'wholesale'
  return 'pm'
}

const getSelectionSet = (itemIndexes) => {
  if (itemIndexes === undefined || itemIndexes === null) return null
  const values = itemIndexes instanceof Set ? Array.from(itemIndexes) : itemIndexes
  return new Set((Array.isArray(values) ? values : []).map((value) => String(value)))
}

export const getRecommendedWholesaleItemIndexes = (comparison = {}) => (
  (comparison?.rows || [])
    .filter((row) => getRecommendedSource(row) === 'wholesale')
    .map((row) => row.itemIndex)
)

export const getWholesaleDeliveryTier = (tierId) => (
  tierById.get(tierId) || tierById.get('large_40_16')
)

export const getRecommendedDeliveryTierId = (comparison = {}, selectedWholesaleItemIndexes) => {
  const selectedSet = getSelectionSet(selectedWholesaleItemIndexes)
  const wholesaleDeliveryProducts = (comparison?.rows || [])
    .filter((row) => {
      if (row.alreadySourced || productCostCents(row.otherProduct) === null) return false
      return selectedSet
        ? selectedSet.has(String(row.itemIndex))
        : getRecommendedSource(row) === 'wholesale'
    })
    .map((row) => row.otherProduct)
    .filter((product) => product && !isNonUnitAccessory(product))

  if (!wholesaleDeliveryProducts.length) return 'no_delivery'
  return wholesaleDeliveryProducts.every(isAirConditioner) ? 'ac_25_5' : 'large_40_16'
}

export const calculateWholesaleDeliveryCents = (quantity, tierId, { omitFirstCharge = false } = {}) => {
  const tier = getWholesaleDeliveryTier(tierId)
  const units = Number(quantity)
  if (!Number.isFinite(units) || units <= 0) return 0
  if (omitFirstCharge) return Math.round(units * tier.additionalUnitCents)
  return Math.round(tier.firstUnitCents + (Math.max(0, units - 1) * tier.additionalUnitCents))
}

export const calculateMarginComparison = (comparison = {}, selectedTierId, selectedWholesaleItemIndexes) => {
  const safeComparison = comparison || {}
  const selectedSet = getSelectionSet(selectedWholesaleItemIndexes)
  const hasExplicitSelection = selectedSet !== null
  const recommendedTierId = getRecommendedDeliveryTierId(
    safeComparison,
    hasExplicitSelection ? selectedSet : undefined
  )
  const tierId = selectedTierId || recommendedTierId
  let pmOrderTotalCents = 0
  let recommendedMerchandiseCents = 0
  let orderableLineCount = 0
  let comparableLineCount = 0
  let pmSourceLineCount = 0
  let wholesaleSourceLineCount = 0
  let wholesaleDeliveryUnitCount = 0
  let pmBaselineComplete = true

  const rows = (safeComparison.rows || []).map((row) => {
    const costs = getRowCosts(row)
    const recommendedSource = getRecommendedSource(row)
    const selectedSource = row.alreadySourced
      ? 'existing'
      : hasExplicitSelection
        ? selectedSet.has(String(row.itemIndex)) && costs.wholesaleLineTotalCents !== null
          ? 'wholesale'
          : costs.pmLineTotalCents !== null ? 'pm' : null
        : recommendedSource
    const quantity = Number(row.quantity) || 0
    const comparable = !row.alreadySourced && costs.pmLineTotalCents !== null && costs.wholesaleLineTotalCents !== null
    const isDeliveryUnit = !isNonUnitAccessory(row.otherProduct)

    if (!row.alreadySourced) {
      orderableLineCount += 1
      if (costs.pmLineTotalCents === null) {
        pmBaselineComplete = false
      } else {
        pmOrderTotalCents += costs.pmLineTotalCents
      }
      if (comparable) comparableLineCount += 1

      if (selectedSource === 'wholesale') {
        wholesaleSourceLineCount += 1
        recommendedMerchandiseCents += costs.wholesaleLineTotalCents
        if (isDeliveryUnit) wholesaleDeliveryUnitCount += quantity
      } else if (selectedSource === 'pm') {
        pmSourceLineCount += 1
        recommendedMerchandiseCents += costs.pmLineTotalCents
      }
    }

    return {
      ...row,
      ...costs,
      comparable,
      recommendedSource,
      selectedSource,
      selectedForWholesale: selectedSource === 'wholesale',
      isDeliveryUnit,
      merchandiseDifferenceCents: comparable
        ? Math.abs(costs.pmLineTotalCents - costs.wholesaleLineTotalCents)
        : null,
    }
  })

  const allOrderableFromWholesale = orderableLineCount > 0 && wholesaleSourceLineCount === orderableLineCount
  const omitFirstCharge = wholesaleSourceLineCount > 0 && wholesaleSourceLineCount < orderableLineCount
  const wholesaleDeliveryCents = calculateWholesaleDeliveryCents(
    wholesaleDeliveryUnitCount,
    tierId,
    { omitFirstCharge }
  )
  const recommendedOrderTotalCents = recommendedMerchandiseCents + wholesaleDeliveryCents
  const rawSavingsCents = pmBaselineComplete ? pmOrderTotalCents - recommendedOrderTotalCents : 0
  const hasWholesaleSavings = wholesaleSourceLineCount > 0 && pmBaselineComplete && rawSavingsCents > 0

  return {
    rows,
    tierId,
    recommendedTierId,
    pmOrderTotalCents,
    recommendedMerchandiseCents,
    recommendedOrderTotalCents,
    wholesaleDeliveryCents,
    wholesaleDeliveryUnitCount,
    orderableLineCount,
    comparableLineCount,
    pmSourceLineCount,
    wholesaleSourceLineCount,
    selectedWholesaleItemIndexes: rows
      .filter((row) => row.selectedForWholesale)
      .map((row) => row.itemIndex),
    allOrderableFromWholesale,
    omitFirstCharge,
    deliveryMode: wholesaleDeliveryUnitCount === 0
      ? 'none'
      : (omitFirstCharge ? 'additional_only' : 'full_order'),
    hasWholesaleSavings,
    savingsCents: Math.max(0, rawSavingsCents),
    pmAdvantageCents: Math.max(0, -rawSavingsCents),
  }
}
